import type { Module } from "vuex";
import { USER_ROLES, type UserActions } from "hoxton-shared/enums/user";

import type { Role } from "@/store/modules/roles";
import type { RootState } from "@/store/types";
import type { OpenOSContext } from "ic-analytics";

import { isViewAllEligibleEmail } from "@/utils/viewAllEligibility";

const storeNamespace = "auth";

const InternalAuthMutation = {
    SetCreated: "setCreated",
    SetIsAuthorized: "setIsAuthorized",
    SetMe: "setMe",
    SetIsWppLogin: "setIsWppLogin",
    SetWppOsContext: "setWppOsContext"
};

export const AuthMutation = {
    SetCreated: `${storeNamespace}/${InternalAuthMutation.SetCreated}`,
    SetIsAuthorized: `${storeNamespace}/${InternalAuthMutation.SetIsAuthorized}`,
    SetMe: `${storeNamespace}/${InternalAuthMutation.SetMe}`,
    SetIsWppLogin: `${storeNamespace}/${InternalAuthMutation.SetIsWppLogin}`,
    SetWppOsContext: `${storeNamespace}/${InternalAuthMutation.SetWppOsContext}`
};

export const AuthGetters = {
    hasScope: `${storeNamespace}/hasScope`,
    isSuperAdmin: `${storeNamespace}/isSuperAdmin`,
    userCan: `${storeNamespace}/userCan`,
    userHasAction: `${storeNamespace}/userHasAction`,
    isWppLogin: `${storeNamespace}/isWppLogin`,
    canUseViewAll: `${storeNamespace}/canUseViewAll`,
    effectiveWppProjectId: `${storeNamespace}/effectiveWppProjectId`
};

export type Scope = {
    campaign: string[] | null;
    client: string[] | null;
    campaignOnlyClient: string[] | null;
    __typename: "UserScope";
};

export type Me = {
    id: string;
    reference: string;
    role: Role;
    scope: Scope;
};

export type State = {
    isAuthorized: boolean;
    me: Me | null;
    isWppLogin: boolean;
    wppOsContext: OpenOSContext | null;
};

const getDefaultState = (): State => ({
    isAuthorized: false,
    me: null,
    isWppLogin: false,
    wppOsContext: null
});

const isSuperAdmin = (state: State) => state.me?.role.name === USER_ROLES.SuperAdmin;

export const canUseViewAll = (isWppLogin: boolean, email: string | null | undefined): boolean =>
    isWppLogin && isViewAllEligibleEmail(email);

export const getEffectiveWppProjectId = ({
    isWppLogin,
    email,
    wppProjectId,
    showAllContent
}: {
    isWppLogin: boolean;
    email: string | null | undefined;
    wppProjectId: string | undefined;
    showAllContent: boolean;
}): string | undefined => (canUseViewAll(isWppLogin, email) && showAllContent ? undefined : wppProjectId);

const authModule: Module<State, RootState> = {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        hasScope:
            state =>
            (scope: { clientId: string; campaignId: string }, exact = true) => {
                // If the user is a Super Admin grant full access
                if (isSuperAdmin(state)) {
                    return true;
                }
                // If actions on the is an array then look for the passed action otherwise permit no access
                const hasClientScope = !!state.me?.scope.client?.includes(scope.clientId);

                if (typeof scope.campaignId === "undefined" && exact) {
                    return hasClientScope;
                }

                let hasCampaignScope = !!state.me?.scope.campaign?.includes(scope.campaignId);

                if (!exact && !hasCampaignScope) {
                    hasCampaignScope = !!state.me?.scope.campaignOnlyClient?.includes(scope.clientId);
                }

                return hasClientScope || hasCampaignScope;
            },

        isSuperAdmin,

        userCan: (state, getters) => (action: UserActions) => {
            // If the user is a Super Admin grant full access
            if (isSuperAdmin(state)) {
                return true;
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
            return getters.userHasAction(action);
        },

        userHasAction: state => (action: UserActions) =>
            state.me?.role && Array.isArray(state.me.role.actions) && state.me.role.actions.includes(action),

        isWppLogin: state => state.isWppLogin,

        // Both inputs are set once per session, so this only recomputes if the auth data itself changes.
        canUseViewAll: state => canUseViewAll(state.isWppLogin, state.me?.id),

        // Reactive rather than set-once: it also depends on ui.showAllContent, which flips with the View All toggle.
        effectiveWppProjectId: (state, _getters, rootState) =>
            getEffectiveWppProjectId({
                isWppLogin: state.isWppLogin,
                email: state.me?.id,
                wppProjectId: state.wppOsContext?.project?.id,
                showAllContent: rootState.ui.showAllContent
            })
    },

    mutations: {
        setIsAuthorized(state, payload: State["isAuthorized"]) {
            state.isAuthorized = payload;
        },

        setMe(state, payload: { me: Me }) {
            state.me = payload.me;
        },

        setIsWppLogin(state, payload: State["isWppLogin"]) {
            state.isWppLogin = payload;
        },

        setWppOsContext(state, payload: State["wppOsContext"]) {
            state.wppOsContext = payload;
        }
    }
};

export default authModule;
